import { useState, useEffect, useRef } from "react";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router";
import { Outlet } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import LoadingButton from "@mui/lab/LoadingButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EnrollmentService from "services/EnrollmentService.js";
import PopUpModal from "components/utils/PopUpModal.js";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import EnhancedTable from "components/utils/Table.js";
import TableRowActionsCell from "components/utils/TableRowActionsCell.js";
import useAPI from "hooks/useAPI.js";
import DateUtils from "utils/DateUtils.js";
import { useOutletContext } from "react-router-dom";

const ENROLLMENT_TYPE_LABELS = {
  STANDARD: "Standard",
  LENS_NFB: "LENS NFB",
};

const tableHeadings = [
  { id: "index", label: "Enrollment" },
  { id: "type", label: "Type" },
  { id: "sessions", label: "Sessions", align: "center" },
  { id: "createdBy", label: "Created By" },
  { id: "createdAtDisplay", label: "Created At" },
  { id: "actions", label: "Actions", align: "center" },
];

export function Enrollments() {
  const [hospitalId] = useOutletContext();

  return <Outlet context={[hospitalId]} />;
}

export function EnrollmentList() {
  const navigate = useNavigate();
  const [hospitalId] = useOutletContext();
  const [enrollmentList, setEnrollmentList] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    total: 0,
  });
  const { patientId } = useParams();

  const deleteEnrollmentModalRef = useRef(null);
  const [deletingEnrollmentId, setDeletingEnrollmentId] = useState("");

  const getEnrollmentsAPI = useAPI(
    EnrollmentService.getPatientEnrollments,
    getEnrollmentsHandler
  );
  const addStandardEnrollmentAPI = useAPI(
    EnrollmentService.createEnrollment,
    addEnrollmentHandler
  );
  const addLensEnrollmentAPI = useAPI(
    EnrollmentService.createEnrollment,
    addEnrollmentHandler
  );
  const deleteEnrollmentAPI = useAPI(
    EnrollmentService.deleteEnrollment,
    deleteEnrollmentHandler,
    true
  );

  function fetchEnrollments(page = pagination.page, size = pagination.size) {
    getEnrollmentsAPI.execute(hospitalId, patientId, page, size);
  }

  function addStandardEnrollment() {
    addStandardEnrollmentAPI.execute(hospitalId, patientId, "STANDARD");
  }

  function addLensEnrollment() {
    addLensEnrollmentAPI.execute(hospitalId, patientId, "LENS_NFB");
  }

  function deleteEnrollment(enrollmentId) {
    deleteEnrollmentAPI.execute(hospitalId, patientId, enrollmentId);
  }

  useEffect(() => {
    if (hospitalId && patientId) {
      fetchEnrollments();
    }
  }, [hospitalId, patientId, pagination.page, pagination.size]);

  function getEnrollmentsHandler(response) {
    const { content, totalElements, number, size } = response.data;
    setEnrollmentList(
      content.map((enrollment, index) =>
        transformEnrollmentData(enrollment, number * size + index + 1)
      )
    );
    setPagination((prev) => ({ ...prev, total: totalElements }));
  }

  function addEnrollmentHandler() {
    setPagination((prev) => {
      const lastPage = Math.max(0, Math.ceil((prev.total + 1) / prev.size) - 1);
      if (lastPage === prev.page) {
        fetchEnrollments(lastPage, prev.size);
      }
      return { ...prev, page: lastPage };
    });
  }

  function deleteEnrollmentHandler() {
    fetchEnrollments();
  }

  function handleRowClick(row) {
    navigate(`${row.id}/sessions`);
  }

  function getEnrollmentTypeChip(enrollmentType) {
    const label = ENROLLMENT_TYPE_LABELS[enrollmentType] || enrollmentType;
    const color = enrollmentType === "LENS_NFB" ? "info" : "default";
    return <Chip label={label} color={color} size="small" variant="outlined" />;
  }

  function transformEnrollmentData(enrollment, index) {
    return {
      id: enrollment.id,
      index,
      type: getEnrollmentTypeChip(enrollment.enrollmentType),
      sessions: enrollment.sessionCount ?? 0,
      createdBy: enrollment.createdByUserFullName || "N/A",
      createdAtDisplay: DateUtils.formatDateTime(enrollment.createdAt),
      actions: (
        <TableRowActionsCell
          align="center"
          actions={{
            startItems: [
              {
                component: (
                  <DeleteOutlineIcon fontSize="inherit" color="error" />
                ),
                onClickHandler: () => {
                  setDeletingEnrollmentId(enrollment.id);
                  deleteEnrollmentModalRef.current.openModal();
                },
              },
            ],
            menuItems: [],
          }}
        />
      ),
      documentLink: true,
    };
  }

  return (
    <Paper elevation={0} className="p-4">
      <Stack direction="row" justifyContent="right" alignItems="center" spacing={2}>
        {addStandardEnrollmentAPI.status === "pending" ? (
          <LoadingButton
            loading
            loadingPosition="start"
            startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
            variant="contained"
          >
            Create Enrollment
          </LoadingButton>
        ) : (
          <Button
            className="text-right"
            onClick={addStandardEnrollment}
            startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
            variant="contained"
            disabled={addLensEnrollmentAPI.status === "pending"}
          >
            Create Enrollment
          </Button>
        )}
        {addLensEnrollmentAPI.status === "pending" ? (
          <LoadingButton
            loading
            loadingPosition="start"
            startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
            variant="contained"
            color="info"
          >
            Create LENS Enrollment
          </LoadingButton>
        ) : (
          <Button
            className="text-right"
            onClick={addLensEnrollment}
            startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
            variant="contained"
            color="info"
            disabled={addStandardEnrollmentAPI.status === "pending"}
          >
            Create LENS Enrollment
          </Button>
        )}
      </Stack>
      {getEnrollmentsAPI.status === "pending" ? (
        <div className="text-center">
          <CircularProgress
            className="text-center"
            sx={{
              my: 20,
            }}
          />
        </div>
      ) : (
        <Box sx={{ mt: 3 }}>
          <EnhancedTable
            headings={tableHeadings}
            rowsData={enrollmentList}
            rowsPerPage={pagination.size}
            page={pagination.page}
            count={pagination.total}
            serverSide
            onPageChange={(nextPage) => {
              setPagination((prev) => ({ ...prev, page: nextPage }));
            }}
            onRowsPerPageChange={(nextSize) => {
              setPagination((prev) => ({ ...prev, size: nextSize, page: 0 }));
            }}
            onRowClick={handleRowClick}
            emptyMessage="No enrollments found."
          />
        </Box>
      )}
      <PopUpModal ref={deleteEnrollmentModalRef} modalWidth={400}>
        <ConfirmActionModal
          content="Do you want to confirm deleting the enrollment?"
          confirmActionHandler={() => {
            deleteEnrollment(deletingEnrollmentId);
            deleteEnrollmentModalRef.current.closeModal();
          }}
          onCloseHandler={() => deleteEnrollmentModalRef.current.closeModal()}
        />
      </PopUpModal>
    </Paper>
  );
}
